import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { SecurityProfileEnum } from "src/app/enums/securityProfile.enum";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { User } from "src/app/models/user.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { UserService } from "src/app/services/user.service";
import { UserRoleService } from "src/app/services/userRole.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import { FormComponent, IFormDialogData } from "src/app/shared/form/form.component";
import { HierarchicalSelectionFormField, SingleSelectionFormField, TextFormField } from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";
export interface IUserFormDialogData extends IFormDialogData {
    organizationLevelIds?: string[];
}

@Component({
  standalone: false,
    selector: 'app-user-dialog-component',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class UserDialogComponent extends FormComponent<User> implements OnInit {
    organizationLevels: OrganizationLevel[];
    userRoles: ILookup[];

    constructor(dialogRef: MatDialogRef<UserDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IUserFormDialogData,
        service: UserService,
        private organizationLevelService: OrganizationLevelService,
        private userRoleService: UserRoleService,
        protected injector: Injector,
    ) {
        super(dialogRef, service, data, "User", injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(),
            userRoles: this.userRoleService.getAll()
        }).subscribe(result => {
            this.entity = result.entity;
            this.organizationLevels = result.organizationLevels;
            this.userRoles = result.userRoles;
            this.initModel();
            this.initFields();
            this.isFormLoading = false;
        },
            error => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            }
        );
    }

    getNewObject(): User {
        return new User();
    }

    initModel(): void {
        if (this.isAdd) {
            this.entity.createAccount = true;
            this.entity.organizationLevelIds = this.entity.organizationLevelIds ?? [];
        }
    }

    validate(): boolean {
        const emailControl = new UntypedFormControl(this.entity.userName, Validators.email);

        if (emailControl.invalid) {
            this.toastService.showError('', 'Invalid email address');
            return false;
        }

        return true;
    }

    initFields(): void {
        const commonFields = [
            new TextFormField({ property: nameOf<User>(x => x.userName), name: 'User Name', columnSpan: new ColumnSpan(1, 1) }),
            new HierarchicalSelectionFormField({
                property: nameOf<User>(x => x.organizationLevelIds),
                options: toOrganizationTree(this.organizationLevels), columnSpan: new ColumnSpan(1, 1),
                name: 'Organization Level', multiple: true, required: true,
            }),
            new SingleSelectionFormField({ property: nameOf<User>(x => x.roleId), options: this.userRoles, name: 'Access Role', columnSpan: new ColumnSpan(1, 1) })
        ];

        const additionalFields = this.isAdd
            ? [
                new TextFormField({ property: nameOf<User>(x => x.firstName), name: 'First Name', columnSpan: new ColumnSpan(1, 1) }),
                new TextFormField({ property: nameOf<User>(x => x.lastName), name: 'Last Name', columnSpan: new ColumnSpan(1, 1) })
            ]
            : [
                new TextFormField({ property: nameOf<User>(x => x.fullName), name: 'Full Name', columnSpan: new ColumnSpan(1, 1) })
            ];

        this.fields = [...commonFields, ...additionalFields];
    }

    onBeforeSave(model: User): void {
        if (this.isAdd) {
            model.securityProfileId = SecurityProfileEnum.Admin;
        }
    }
}
